import { useEffect, useRef, type FocusEvent, type KeyboardEvent } from 'react'
import type { ContributionAmountKind } from '@klear/constants/document-processing'
import { Button } from '@klear/ui/components/button'
import { Input } from '@klear/ui/components/input'
import { Textarea } from '@klear/ui/components/textarea'
import { ChoiceGroup } from '@/app/document-review/choice-group'
import { DATE_FIELDS } from '@/app/document-review/field-edit'

const AMOUNT_KIND_CHOICES = [
  { value: 'total', label: 'Total' },
  { value: 'change', label: 'Change' }
] as const satisfies readonly { value: ContributionAmountKind; label: string }[]

function amountKindHint(value: ContributionAmountKind | null): string {
  if (value === 'total') return "The record's amount becomes this figure."
  if (value === 'change') return "This figure is added to the record's amount. Use a minus sign for a credit."
  return "Total when the page states the record's whole amount. Change when it adds or takes some off."
}

export type FieldEditorProps = {
  field: string
  label: string
  value: string
  disabled: boolean
  onChange: (value: string) => void
  onCommit: () => void
  onLeave: () => void
  onCancel: () => void
  onKeep?: () => void
  amountKind?: { value: ContributionAmountKind | null; onChange: (value: ContributionAmountKind) => void }
}

const PLACEHOLDERS: Readonly<Record<string, string>> = {
  amount: 'USD 1250.00',
  currency: 'USD',
  incoterm: 'DAP',
  effectiveDate: 'YYYY-MM-DD',
  endDate: 'YYYY-MM-DD'
}

export function FieldEditor({
  field,
  label,
  value,
  disabled,
  onChange,
  onCommit,
  onLeave,
  onCancel,
  onKeep,
  amountKind
}: FieldEditorProps) {
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null)
  const container = useRef<HTMLDivElement>(null)
  const discarding = useRef(false)
  const committed = useRef(false)
  const multiline = field === 'notes'

  useEffect(() => {
    ref.current?.focus()
    ref.current?.select()
  }, [])

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    event.stopPropagation()
    if (event.key === 'Escape') {
      event.preventDefault()
      discarding.current = true
      onCancel()
      return
    }
    if (event.key === 'Enter' && !(multiline && event.shiftKey)) {
      event.preventDefault()
      committed.current = true
      onCommit()
    }
  }

  const onBlur = (event: FocusEvent<HTMLElement>) => {
    if (container.current?.contains(event.relatedTarget)) return
    if (!discarding.current && !committed.current) onLeave()
  }

  const change = (next: string) => {
    committed.current = false
    onChange(next)
  }

  return (
    <div ref={container} className="mt-1.5 flex flex-col gap-1" onBlur={onBlur}>
      {amountKind !== undefined && (
        <div
          className="flex flex-col items-start gap-1"
          onKeyDown={onKeyDown}
          onMouseDown={(event) => event.preventDefault()}
        >
          <ChoiceGroup<ContributionAmountKind | null>
            label="Read this figure as"
            choices={AMOUNT_KIND_CHOICES}
            value={amountKind.value}
            disabled={disabled}
            onChange={(next) => {
              if (next === null) return
              committed.current = false
              amountKind.onChange(next)
            }}
          />
          <p className="text-muted-foreground text-xs">{amountKindHint(amountKind.value)}</p>
        </div>
      )}
      {multiline ? (
        <Textarea
          ref={ref}
          aria-label={label}
          value={value}
          onChange={(event) => change(event.target.value)}
          onKeyDown={onKeyDown}
          maxLength={4000}
          rows={3}
          disabled={disabled}
        />
      ) : (
        <Input
          ref={ref}
          aria-label={label}
          value={value}
          onChange={(event) => change(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={PLACEHOLDERS[field]}
          maxLength={field === 'currency' ? 3 : DATE_FIELDS.includes(field) ? 10 : 500}
          size="sm"
          disabled={disabled}
          className="font-medium"
        />
      )}
      {(multiline || onKeep !== undefined) && (
        <div className="flex items-center justify-between gap-2">
          {multiline && <p className="text-muted-foreground text-xs">Shift+Enter starts a new line.</p>}
          {onKeep !== undefined && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="ml-auto h-7 px-2 text-xs"
              disabled={disabled}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                discarding.current = true
                onKeep()
              }}
            >
              Keep current value
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
